// TODO: Move this into /marketplace/src/checkout/actions, build-order.test.js is located there
import _ from 'lodash';
import { toJS } from 'mobx';

import { getPersistedValue } from 'shared/utils/persisted-values';
import { DPAY_SESSION_TOKEN_KEY } from 'src/payments/constants';
import { hasSaleSpecialForMenuType } from 'shared/helpers/specials';
import { removeTypename } from 'shared/helpers/utils';
import { isDutchiePlusKioskOrder } from 'shared/helpers/checkout';
import { mapOrderTypeToPreOrderType, determinePlatformType } from 'shared/helpers/enum-mappings';
import { isTippableMethod } from 'src/checkout/components/sections/payment/shared/utils';

import { ChannelSources } from './constants';

/*
 * called via src/checkout/actions/onClickSubmit.js
 */
export default function buildOrder({
  Cart,
  UI,
  User,
  Checkout,
  considerPayerForIncentives = false,
  termsAccepted = null,
  tipEligibilityValidationEnabled = false,
  isKioskPhoneOnlySignup = false,
}) {
  const { isVariant, checkoutId } = Checkout;
  const { dispensary } = Cart.order;
  const { removeMedicalCardFieldsAtCheckout } = dispensary;
  const sendPaysafeInput = !!(
    (dispensary.paymentTypesAccepted?.payOnlineMerrco ?? dispensary.payOnlineMerrco) &&
    Cart.order.paymentMethod === 'payOnlineMerrco'
  );
  let guestCustomerFields = null;

  if (Cart.isGuestOrder || isVariant('kiosk')) {
    guestCustomerFields = Cart.order.guestCustomer;
  }

  // if we have an external order put those values on the guestCustomer field
  // if the MSO gives us that info, lets store it.
  if (User.hasExternalUser) {
    guestCustomerFields = toJS(User.userDetailsFromExternalSource);
  }

  // If a user clicks the box to save info, then enters a password, then unchecks the box,
  // don't send the password to the server.
  if (!Cart.saveGuestInfo && guestCustomerFields) {
    delete guestCustomerFields.password;
  }

  // this serializes the utmData to ensure that it is not an array of duplicated values, but a string of that value that
  // was duplicated
  const sessionStoredUtmData = getPersistedValue('utmSessionData', { parameters: {} });
  const sessionUtmParameters = _.get(sessionStoredUtmData, 'parameters', {});

  const isKioskProEnabled = dispensary?.kioskConfig?.type === 'PRO';

  const priceCartEnabled = dispensary?.storeSettings?.enableLLxSaleDiscountSync;

  const couponObject = priceCartEnabled
    ? { couponCode: _.get(Cart.order, 'coupon.code') }
    : { couponId: _.get(Cart.order, 'coupon._id') };

  // Kiosk Pro "logged-in" checkout relies on server-side auth (cookie/session).
  // If the client believes it's logged in but the token is expired, don't mark this as a non-guest order.
  if (isVariant('kiosk') && isKioskProEnabled && User.isLoggedIn && !User.expiredToken) {
    Cart.setKioskProUserOrder(true);
  }
  // Determine order type for discount logic
  let orderTypeForEnum = 'pickup'; // Default
  if (Cart.isDelivery) {
    orderTypeForEnum = 'delivery';
  } else if (Cart.isDriveThruPickup) {
    orderTypeForEnum = 'driveThruPickup';
  } else if (Cart.isCurbsidePickup) {
    orderTypeForEnum = 'curbsidePickup';
  }

  const preOrderType = mapOrderTypeToPreOrderType(orderTypeForEnum);
  const shouldSendTip =
    !tipEligibilityValidationEnabled || isTippableMethod(Cart.order.paymentMethod, orderTypeForEnum, dispensary);
  const platformType = determinePlatformType(UI);
  const hasRoundupForCharityAmount = (Cart.costBreakdown?.roundupForCharityAmount ?? 0) > 0;
  const roundupForCharityEnabled =
    !!Cart.charityFeeDonationId && !!Cart.charityRoundupSelected && !!hasRoundupForCharityAmount;

  // if possible, dont destructure values here. Logic above the fold could be altering them.
  return {
    proceed: true,
    order: _.omitBy(
      removeTypename({
        dispensaryId: dispensary.id,
        products: _.map(_.values(Cart.order.cart), (item) =>
          _.omitBy(
            {
              id: item.product._id,
              quantity: parseInt(item.quantity, 10),
              option: item.option,
              additionalOption: item.additionalOption,
              special: hasSaleSpecialForMenuType({ product: item.product, medicalOrder: Cart.order.medicalOrder }),
              rawOption: item.rawOption,
            },
            _.isNil
          )
        ),
        tip: shouldSendTip ? Cart.order.tipValue : undefined,
        ...couponObject,
        // ECOMM-9911: pick only schema-defined fields. `Cart.paysafeInput` carries an
        // additional `tokenizedAt` field for the client-side freshness check; the GraphQL
        // `paysafeInput` input type only accepts `token` and `zip`, and graphql-js coercion
        // rejects unknown fields. Leaving `tokenizedAt` in the wire payload was the most
        // plausible cause of INC-1429.
        paysafeInput: sendPaysafeInput ? _.pick(_.get(Cart.order, 'paysafeInput'), ['token', 'zip']) : undefined,
        ...(roundupForCharityEnabled
          ? { feeDonations: [{ feeDonationId: Cart.charityFeeDonationId }], roundupForCharityEnabled }
          : {}),
        paymentMethod: Cart.order.paymentMethod,
        paymentMethodId: Cart.paymentMethodId,
        considerPayerForIncentives,
        utmData: sessionUtmParameters,
        embedded: isVariant(['embedded', 'store-front']),
        variantSource: Checkout.variant,
        medicalOrder: Cart.order.medicalOrder ?? false,
        medicalCard: removeMedicalCardFieldsAtCheckout
          ? null
          : _.omit(_.get(Cart.order, 'medicalCard') || undefined, ['dateOfBirth']),
        driversLicense: _.get(Cart.order, 'driversLicense'),
        driversLicenseExpirationDate: _.get(Cart.order, 'driversLicenseExpirationDate'),
        photoId: _.get(Cart.order, 'photoId', User.profile.photoId),
        deliveryOption: Cart.isDelivery,
        address: UI.formattedAddress,
        location: _.pick(UI.currentAddress, [
          'ln1',
          'ln2',
          'city',
          'place',
          'state',
          'zipcode',
          'lat',
          'lng',
          'streetNumber',
          'streetName',
          'country',
        ]),
        specialInstructions: _.get(Cart.order, 'specialInstructions', ''),
        isPreviewOrder: UI.previewMode,
        isKioskOrder: isVariant('kiosk') || isDutchiePlusKioskOrder(Cart.orderType, UI.isPlus),
        ...(isKioskPhoneOnlySignup && { isKioskPhoneOnlySignup: true }),
        isGuestOrder: isVariant('kiosk') && !isKioskProEnabled ? true : Cart.isGuestOrder,
        isExternalOrder: User.hasExternalUser,
        isAfterHoursOrder: Cart.isAfterHoursOrder,
        isCurbsidePickupOrder: Cart.isCurbsidePickup,
        isDriveThruPickupOrder: Cart.isDriveThruPickup,
        guestCustomer: guestCustomerFields,
        checkoutToken: Cart.order.checkoutToken,
        // PBB conversion-funnel join key. Sent on every order (harmless optional
        // field); the server stamps it onto the order only when the rollout flag is on. Empty
        // string coerces to undefined so _.omitBy drops it when there's no PBB session.
        dutchiePaySessionToken: getPersistedValue(DPAY_SESSION_TOKEN_KEY, '') || undefined,
        expectedTotal: Cart.costBreakdown.total,
        destinationTerminal: Cart.order.destinationTerminal,
        originKioskId: Cart.originKioskId,
        customerState: Cart.customerState,
        appliedRewards: _.map(Cart.order.appliedRewards, (reward) =>
          _.pick(reward, ['id', 'available', 'operator', 'value', 'brand', 'copy', 'rewardType', 'discountId'])
        ),
        reservation: _.pick(Cart.order.reservation, ['startTimeISO', 'endTimeISO', 'slotId']),
        reservationType: Cart.reservationType,
        hypurAccessToken: Cart.hypurAccessToken,
        monerisInput: _.isEmpty(Cart.monerisInput) ? null : Cart.monerisInput,
        saveGuestInfo: Cart.saveGuestInfo,
        checkoutId,
        isPTPOT: true,
        channelSource: isVariant('mobile-ecomm') ? ChannelSources.retailerMobile : null,
        schedulingData: toJS(Cart.schedulingData),
        loyaltyPointsApplied: Cart.order.loyaltyPointsApplied,
        bypassCanadaDeliveryTax: Cart.bypassCanadaDeliveryTax,
        posCustomerId: Cart.posCustomerId ?? null,
        preOrderType,
        platformType,
        termsAccepted,
      }),
      _.isNil
    ),
  };
}
